import React, { useEffect, useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { HiOutlineMinusCircle, HiPlus } from 'react-icons/hi'

import {
  Checkbox,
  Flex,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'

import { formatItemsForParent } from '../utils/utils'

interface CheckpointChecklistIProps {
  isInEditMode: boolean
  hasDuplicateItems: boolean
  existingValues: { value: string; id: string }[]
  areTasksRequired?: boolean
  onRequiredChange: (required: boolean) => void
  onUpdate: (items: { id?: string; value: string }[]) => void
}

export const CheckpointChecklist = ({
  existingValues,
  hasDuplicateItems,
  isInEditMode,
  areTasksRequired = false,
  onRequiredChange,
  onUpdate,
}: CheckpointChecklistIProps) => {
  const { shouldEnableCheckpointChecklist } = usePermissions()
  const [rows, setRows] = useState(existingValues)
  const [isInitialized, setIsInitialized] = useState(false)
  const MAX_CHECKLIST_LENGTH = 5
  const MAX_CHARACTER_LENGTH = 100
  const isDisabled = !isInEditMode || !shouldEnableCheckpointChecklist

  const addRow = () => {
    setRows([...rows, { id: `temp-${Math.random()}`, value: '' }])
  }

  // Initialize rows from existingValues only once
  useEffect(() => {
    if (!isInitialized && existingValues.length >= 0) {
      setRows(existingValues)
      setIsInitialized(true)
    }
  }, [existingValues, isInitialized])

  // Call onUpdate when rows change
  useEffect(() => {
    const formattedItems = formatItemsForParent(rows)
    onUpdate(formattedItems)
  }, [rows])

  const removeRow = (id: string) => {
    setRows(rows.filter((row) => row.id !== id))
  }

  const updateRow = (id: string, field: string, value: string) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    )
  }

  return (
    <VStack align='stretch' py={8} spacing={6}>
      <Flex align='center' justify='space-between'>
        <Text color='gray' fontSize='18' fontWeight='bold'>
          Checkpoint Checklist
        </Text>
        <IconButton
          aria-label='Add checklist item'
          colorScheme='blue'
          disabled={
            !isInEditMode ||
            rows.length === MAX_CHECKLIST_LENGTH ||
            !shouldEnableCheckpointChecklist
          }
          icon={<HiPlus size='20px' />}
          onClick={addRow}
          size='sm'
        />
      </Flex>
      <VStack align='stretch' spacing={3}>
        <Flex align='center'>
          <Checkbox
            isChecked={areTasksRequired}
            isDisabled={!isInEditMode || !shouldEnableCheckpointChecklist}
            onChange={(e) => onRequiredChange?.(e.target.checked)}
            size='md'
          >
            <Text fontSize='md' fontWeight='semi-bold'>
              Required
            </Text>
          </Checkbox>
        </Flex>
        {rows.map((row) => (
          <Flex align='center' gap={3} key={row.id}>
            <Input
              _placeholder={{ color: 'gray.500' }}
              borderRadius='md'
              isDisabled={isDisabled}
              maxLength={MAX_CHARACTER_LENGTH}
              maxW='300px'
              onChange={(e) => updateRow(row.id, 'value', e.target.value)}
              placeholder='Enter Checklist Item'
              size='md'
              value={row.value}
              variant='outline'
            />

            <IconButton
              aria-label='Remove checklist'
              color='black'
              icon={<HiOutlineMinusCircle size='20px' />}
              isDisabled={isDisabled}
              onClick={() => removeRow(row.id)}
              size='sm'
              variant='ghost'
            />
          </Flex>
        ))}

        {hasDuplicateItems && (
          <Text color='incident.active.100' fontSize='sm' fontWeight={500}>
            All checklist items must be unique
          </Text>
        )}
      </VStack>

      {rows.length === 0 && (
        <Text color='gray.400' fontSize='sm' textAlign='center'>
          No checklist items
        </Text>
      )}
    </VStack>
  )
}
